import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import DashboardLayout from '@/components/layout/DashboardLayout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragOverlay,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';

import ElementSidebar from './ElementSidebar';
import ModuleCanvas from './ModuleCanvas';
import ElementPropertiesPanel from './ElementPropertiesPanel';
import CourseSettingsModal from './CourseSettingsModal';
import { useElementSaveQueue } from './useSaveQueue';
import { ElementPreviewDialog } from './ElementPreviewDialog';
import { ModuleDelayDialog } from './ModuleDelayDialog';
import { ImportModuleDialog } from './ImportModuleDialog';
import { ReviewBanner, SubmitReviewDialog } from './ReviewControls';

import {
  Save,
  Eye,
  Settings,
  ChevronLeft,
  Loader2,
  Undo2,
  Redo2,
  Globe,
  GlobeLock,
  Check,
  AlertTriangle,
  CloudOff,
  Send,
} from 'lucide-react';

const CourseBuilder = () => {
  const { courseId } = useParams();
  const navigate = useNavigate();
  const { api, user } = useAuth();
  const isNewCourse = !courseId || courseId === 'new';

  // Detect if we came from admin or cms path
  const isAdmin = window.location.pathname.startsWith('/admin');
  const coursesListPath = isAdmin ? '/admin/courses' : '/cms/courses';
  const courseEditPath = (id) => isAdmin ? `/admin/courses/${id}/edit` : `/cms/courses/${id}/edit`;

  const [course, setCourse] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [previewElement, setPreviewElement] = useState(null);
  const [delayModule, setDelayModule] = useState(null);
  const [importOpen, setImportOpen] = useState(false);
  const [selectedElement, setSelectedElement] = useState(null);
  const [selectedModule, setSelectedModule] = useState(null);
  const [showSettings, setShowSettings] = useState(isNewCourse);
  const [previewMode, setPreviewMode] = useState(false);
  const canPublish = user?.role === 'super_admin';
  const [submitOpen, setSubmitOpen] = useState(false);
  const [reviewTick, setReviewTick] = useState(0);

  // Drag and drop sensors
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 8 }
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Fetch course data
  useEffect(() => {
    if (!isNewCourse) {
      fetchCourse();
    } else {
      setCourse({
        title: 'Untitled Course',
        description: '',
        category: 'Other',
        modules: [],
        status: 'draft',
      });
      setLoading(false);
    }
  }, [courseId]);

  const fetchCourse = async () => {
    try {
      const response = await api.get(`/builder/courses/${courseId}`);
      setCourse(response.data);
    } catch (error) {
      toast.error('Failed to load course');
      if (!course) navigate(coursesListPath);
    } finally {
      setLoading(false);
    }
  };

  const saveQueue = useElementSaveQueue(api, course?.id);
  const autoSaveStatus = saveQueue.status;

  // Every explicit action commits pending edits first; returns false (with a red error) if anything failed
  const commitAll = async () => {
    const ok = await saveQueue.flushAll();
    if (!ok) toast.error('Some changes could not be saved. Nothing was published — please retry.', { id: 'builder-commit-error' });
    return ok;
  };

  const leaveBuilder = async () => {
    if (saveQueue.hasUnsaved() && !(await commitAll())) return;
    navigate(coursesListPath);
  };

  // Create course
  const handleCreateCourse = async (courseData) => {
    if (!courseData.title?.trim()) {
      toast.error('Course title is required');
      return;
    }
    if (!courseData.description?.trim()) {
      toast.error('Course description is required');
      return;
    }
    try {
      setSaving(true);
      const response = await api.post('/builder/courses', courseData);
      setCourse(response.data);
      navigate(courseEditPath(response.data.id), { replace: true });
      toast.success('Course created successfully');
      setShowSettings(false);
    } catch (error) {
      const detail = error.response?.data?.detail;
      if (Array.isArray(detail)) {
        const fields = detail.map(d => d.loc?.slice(-1)[0]).filter(Boolean).join(', ');
        toast.error(`Missing required fields: ${fields}`);
      } else {
        toast.error(typeof detail === 'string' ? detail : 'Failed to create course');
      }
      console.error('Create course error:', error.response?.data || error);
    } finally {
      setSaving(false);
    }
  };

  // Update course
  const handleUpdateCourse = async (courseData) => {
    try {
      setSaving(true);
      const response = await api.put(`/builder/courses/${course.id}`, courseData);
      setCourse(prev => ({ ...prev, ...response.data }));
      toast.success('Course updated');
      setShowSettings(false);
    } catch (error) {
      const detail = error.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Failed to update course');
      console.error('Update course error:', error.response?.data || error);
    } finally {
      setSaving(false);
    }
  };

  // Add module
  const handleAddModule = async () => {
    if (!course?.id) return;

    try {
      const response = await api.post(`/builder/courses/${course.id}/modules`, {
        title: `Module ${(course.modules?.length || 0) + 1}`,
        description: '',
        course_id: course.id,
        order: course.modules?.length || 0,
      });

      setCourse(prev => ({
        ...prev,
        modules: [...(prev.modules || []), { ...response.data, elements: [] }],
      }));

      toast.success('Module added');
    } catch (error) {
      toast.error('Failed to add module');
    }
  };

  // Delete module
  const handleDeleteModule = async (moduleId) => {
    if (!window.confirm('Delete this module and all its elements?')) return;

    try {
      await api.delete(`/builder/courses/${course.id}/modules/${moduleId}`);
      setCourse(prev => ({
        ...prev,
        modules: prev.modules.filter(m => m.id !== moduleId),
      }));
      if (selectedModule?.id === moduleId) {
        setSelectedModule(null);
        setSelectedElement(null);
      }
      toast.success('Module deleted');
    } catch (error) {
      toast.error('Failed to delete module');
    }
  };

  // Add element
  const handleAddElement = async (moduleId, elementType) => {
    try {
      const response = await api.post(`/builder/modules/${moduleId}/elements`, {
        title: getDefaultElementTitle(elementType),
        type: elementType,
        module_id: moduleId,
        order: course.modules.find(m => m.id === moduleId)?.elements?.length || 0,
        config: getDefaultConfig(elementType),
        is_required: elementType === 'trainer_tip' ? false : true,
      });

      setCourse(prev => ({
        ...prev,
        modules: prev.modules.map(m =>
          m.id === moduleId
            ? { ...m, elements: [...(m.elements || []), response.data] }
            : m
        ),
      }));

      setSelectedElement(response.data);
      setSelectedModule(course.modules.find(m => m.id === moduleId));

      toast.success('Element added');
    } catch (error) {
      toast.error('Failed to add element');
    }
  };

  const handleDuplicateModule = async (moduleId) => {
    if (!(await commitAll())) return;
    try {
      const res = await api.post(`/builder/courses/${course.id}/modules/${moduleId}/duplicate`);
      await fetchCourse();
      toast.success(`Module duplicated with ${res.data.elements} element(s)`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to duplicate module');
    }
  };

  const handleDuplicateElement = async (moduleId, elementId) => {
    if (!(await commitAll())) return;
    try {
      await api.post(`/builder/modules/${moduleId}/elements/${elementId}/duplicate`);
      await fetchCourse();
      toast.success('Element duplicated');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to duplicate element');
    }
  };

  // Delete element
  const handleDeleteElement = async (moduleId, elementId) => {
    try {
      await api.delete(`/builder/modules/${moduleId}/elements/${elementId}`);
      setCourse(prev => ({
        ...prev,
        modules: prev.modules.map(m =>
          m.id === moduleId
            ? { ...m, elements: m.elements.filter(e => e.id !== elementId) }
            : m
        ),
      }));
      if (selectedElement?.id === elementId) {
        setSelectedElement(null);
      }
      toast.success('Element deleted');
    } catch (error) {
      toast.error('Failed to delete element');
    }
  };

  // Update element (auto-save)
  const handleUpdateElement = async (moduleId, elementId, updates) => {
    // Optimistically update local state
    setCourse(prev => ({
      ...prev,
      modules: prev.modules.map(m =>
        m.id === moduleId
          ? {
              ...m,
              elements: m.elements.map(e =>
                e.id === elementId ? { ...e, ...updates } : e
              ),
            }
          : m
      ),
    }));

    if (selectedElement?.id === elementId) {
      setSelectedElement(prev => ({ ...prev, ...updates }));
    }

    // Queued per element (edits to different elements never cancel each other)
    saveQueue.queue(moduleId, elementId, updates);
  };

  // Handle drag end for reordering
  const handleDragEnd = async (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const activeData = active.data.current;
    const overData = over.data.current;

    // Module reordering
    if (activeData?.type === 'module' && overData?.type === 'module') {
      const oldIndex = course.modules.findIndex(m => m.id === active.id);
      const newIndex = course.modules.findIndex(m => m.id === over.id);

      const newModules = arrayMove(course.modules, oldIndex, newIndex);
      setCourse(prev => ({ ...prev, modules: newModules }));

      try {
        await api.post(`/builder/courses/${course.id}/modules/reorder`,
          newModules.map((m, i) => ({ id: m.id, order: i }))
        );
      } catch (error) {
        toast.error('Module order was NOT saved. Please try again.');
        fetchCourse();
      }
    }

    // Element reordering within same module
    if (activeData?.type === 'element' && overData?.type === 'element') {
      const moduleId = activeData.moduleId;
      const module = course.modules.find(m => m.id === moduleId);
      
      if (module && overData.moduleId === moduleId) {
        const oldIndex = module.elements.findIndex(e => e.id === active.id);
        const newIndex = module.elements.findIndex(e => e.id === over.id);

        const newElements = arrayMove(module.elements, oldIndex, newIndex);
        
        setCourse(prev => ({
          ...prev,
          modules: prev.modules.map(m =>
            m.id === moduleId ? { ...m, elements: newElements } : m
          ),
        }));

        try {
          await api.post(`/builder/modules/${moduleId}/elements/reorder`,
            newElements.map((e, i) => ({ id: e.id, order: i }))
          );
        } catch (error) {
          toast.error('Element order was NOT saved. Please try again.');
          fetchCourse();
        }
      }
    }
  };

  // Publish/Unpublish
  const handlePublish = async () => {
    try {
      setSaving(true);
      if (!(await commitAll())) return;
      if (course.status === 'published') {
        await api.post(`/builder/courses/${course.id}/unpublish`);
        setCourse(prev => ({ ...prev, status: 'draft' }));
        toast.success('Course unpublished');
      } else {
        await api.post(`/builder/courses/${course.id}/publish`);
        setCourse(prev => ({ ...prev, status: 'published' }));
        toast.success('Course published!');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update course status');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <DashboardLayout requiredRoles={['super_admin', 'content_manager']}>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-[#20acf6]" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout requiredRoles={['super_admin', 'content_manager']}>
      <div className="h-[calc(100vh-4rem)] flex flex-col" data-testid="course-builder">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200 bg-white">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="sm"
              onClick={leaveBuilder}
              data-testid="back-to-courses-btn"
            >
              <ChevronLeft className="h-4 w-4 mr-1" />
              Back to Courses
            </Button>
            <div>
              <h1 className="text-lg font-semibold text-[#2a3e5d] line-clamp-1">
                {course?.title || 'New Course'}
              </h1>
              <div className="flex items-center gap-2 mt-0.5">
                <Badge
                  className={course?.status === 'published' 
                    ? 'bg-green-100 text-green-800'
                    : 'bg-yellow-100 text-yellow-800'
                  }
                >
                  {course?.status || 'draft'}
                </Badge>
                {saving && (
                  <span className="text-xs text-slate-500 flex items-center gap-1">
                    <Loader2 className="h-3 w-3 animate-spin" />
                    Saving...
                  </span>
                )}
                {!saving && autoSaveStatus === 'saving' && (
                  <span className="text-xs text-slate-500 flex items-center gap-1" data-testid="autosave-saving">
                    <Loader2 className="h-3 w-3 animate-spin" />
                    Auto-saving...
                  </span>
                )}
                {!saving && autoSaveStatus === 'pending' && (
                  <span className="text-xs text-slate-500" data-testid="autosave-pending">Unsaved changes…</span>
                )}
                {!saving && autoSaveStatus === 'error' && (
                  <button type="button" onClick={() => saveQueue.flushAll()} className="text-xs text-red-600 font-semibold flex items-center gap-1" data-testid="autosave-error">
                    <CloudOff className="h-3 w-3" /> Not saved — click to retry
                  </button>
                )}
                {!saving && autoSaveStatus === 'saved' && (
                  <span className="text-xs text-green-600 flex items-center gap-1" data-testid="autosave-saved">
                    <Check className="h-3 w-3" />
                    Saved
                  </span>
                )}
              </div>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowSettings(true)}
              data-testid="builder-settings-button"
            >
              <Settings className="h-4 w-4 mr-1" />
              Settings
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPreviewMode(!previewMode)}
            >
              <Eye className="h-4 w-4 mr-1" />
              Preview
            </Button>
            {course?.id && (
              <>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={async () => {
                    try {
                      setSaving(true);
                      if (!(await commitAll())) return;
                      toast.success(course.status === 'published'
                        ? 'All changes saved (course stays published — use Unpublish to take it offline)'
                        : 'Draft saved — all changes are stored');
                    } catch (error) {
                      const detail = error.response?.data?.detail;
                      toast.error(typeof detail === 'string' ? detail : 'Failed to save draft');
                    } finally {
                      setSaving(false);
                    }
                  }}
                  disabled={saving}
                  data-testid="save-draft-btn"
                >
                  <Save className="h-4 w-4 mr-1" />
                  {course.status === 'published' ? 'Save' : 'Save Draft'}
                </Button>
                {canPublish ? (
                <Button
                  size="sm"
                  onClick={async () => {
                    try {
                      setSaving(true);
                      if (!(await commitAll())) return;
                      await api.post(`/builder/courses/${course.id}/publish`);
                      setCourse(prev => ({ ...prev, status: 'published' }));
                      toast.success(course.status === 'published' ? 'Changes are now live' : 'Course published!');
                      navigate(coursesListPath);
                    } catch (error) {
                      toast.error(error.response?.data?.detail || 'Failed to publish');
                    } finally {
                      setSaving(false);
                    }
                  }}
                  disabled={saving}
                  className="bg-green-600 hover:bg-green-700"
                  data-testid="publish-btn"
                >
                  <Globe className="h-4 w-4 mr-1" />
                  {course.status === 'published' ? 'Publish changes' : 'Publish'}
                </Button>
                ) : (
                <Button
                  size="sm"
                  onClick={async () => {
                    setSaving(true);
                    const ok = await commitAll();
                    setSaving(false);
                    if (ok) setSubmitOpen(true);
                  }}
                  disabled={saving}
                  className="bg-green-600 hover:bg-green-700"
                  data-testid="submit-review-btn"
                >
                  <Send className="h-4 w-4 mr-1" />
                  Submit for approval
                </Button>
                )}
                {canPublish && course.status === 'published' && (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={handlePublish}
                    disabled={saving}
                    className="text-yellow-700 border-yellow-300 hover:bg-yellow-50"
                    data-testid="unpublish-btn"
                  >
                    <GlobeLock className="h-4 w-4 mr-1" />
                    Unpublish
                  </Button>
                )}
              </>
            )}
          </div>
        </div>

        {course.id && <ReviewBanner courseId={course.id} refreshKey={`${reviewTick}-${autoSaveStatus}-${course.status}`} />}
        <SubmitReviewDialog open={submitOpen} courseId={course.id} onClose={() => setSubmitOpen(false)} onSubmitted={() => { setSubmitOpen(false); setReviewTick(t => t + 1); }} />
        {saveQueue.recovered && (
          <div className="flex flex-wrap items-center gap-3 px-6 py-3 bg-amber-50 border-b border-amber-200 text-sm" data-testid="builder-recovery-banner">
            <AlertTriangle className="h-4 w-4 text-amber-600" />
            <span className="text-amber-900">
              We found {Object.keys(saveQueue.recovered).length} unsaved change(s) from your last session in this browser
              ({new Date(Math.max(...Object.values(saveQueue.recovered).map(r => r.ts || 0))).toLocaleString()}).
            </span>
            <Button size="sm" className="bg-amber-600 hover:bg-amber-700" data-testid="builder-recovery-restore-btn"
              onClick={async () => { if (await saveQueue.restoreRecovered()) { toast.success('Unsaved changes restored'); fetchCourse(); } }}>
              Restore
            </Button>
            <Button size="sm" variant="outline" onClick={saveQueue.discardRecovered} data-testid="builder-recovery-discard-btn">Discard</Button>
          </div>
        )}

        {/* Main Builder Area */}
        <div className="flex-1 flex overflow-hidden">
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            {/* Left Sidebar - Element Types */}
            <ElementSidebar
              onAddElement={(type) => {
                if (selectedModule) {
                  handleAddElement(selectedModule.id, type);
                } else if (course?.modules?.length > 0) {
                  handleAddElement(course.modules[0].id, type);
                } else {
                  toast.error('Add a module first');
                }
              }}
            />

            {/* Center Canvas - Course Structure */}
            <ModuleCanvas
              course={course}
              selectedModule={selectedModule}
              selectedElement={selectedElement}
              onSelectModule={setSelectedModule}
              onSelectElement={(element, module) => {
                setSelectedElement(element);
                setSelectedModule(module);
              }}
              onAddModule={handleAddModule}
              onImportModule={() => setImportOpen(true)}
              onDeleteModule={handleDeleteModule}
              onUpdateModule={async (moduleId, updates) => {
                try {
                  await api.put(`/builder/courses/${course.id}/modules/${moduleId}`, updates);
                  if ('unlock_after_days' in updates) toast.success(updates.unlock_after_days ? `Module unlocks ${updates.unlock_after_days} days after enrolment` : 'Module delay removed');
                  setCourse(prev => ({
                    ...prev,
                    modules: prev.modules.map(m =>
                      m.id === moduleId ? { ...m, ...updates } : m
                    ),
                  }));
                } catch (error) {
                  toast.error(error.response?.data?.detail || 'Module change was NOT saved');
                }
              }}
              onAddElement={handleAddElement}
              onDeleteElement={handleDeleteElement}
              onDuplicateModule={handleDuplicateModule}
              onDuplicateElement={handleDuplicateElement}
              onPreviewElement={setPreviewElement}
              onEditDelay={setDelayModule}
              previewMode={previewMode}
            />

            {/* Right Panel - Element Properties */}
            <ElementPropertiesPanel
              element={selectedElement}
              module={selectedModule}
              onUpdate={(updates) => {
                if (selectedElement && selectedModule) {
                  handleUpdateElement(selectedModule.id, selectedElement.id, updates);
                }
              }}
              onClose={() => {
                setSelectedElement(null);
              }}
            />
          </DndContext>
        </div>

        <ImportModuleDialog
          open={importOpen}
          onOpenChange={setImportOpen}
          currentCourseId={course?.id}
          onImport={async (m) => {
            if (!(await commitAll())) return;
            try {
              const res = await api.post(`/builder/courses/${course.id}/modules/import`, { source_module_id: m.id });
              await fetchCourse();
              toast.success(`Imported "${res.data.title}" with ${res.data.elements} element(s)`);
            } catch (error) {
              toast.error(error.response?.data?.detail || 'Import failed');
            }
          }}
        />
        <ElementPreviewDialog element={previewElement} onClose={() => setPreviewElement(null)} />
        <ModuleDelayDialog
          module={delayModule}
          onClose={() => setDelayModule(null)}
          onSave={async (days) => {
            const m = delayModule;
            setDelayModule(null);
            try {
              await api.put(`/builder/courses/${course.id}/modules/${m.id}`, { unlock_after_days: days });
              setCourse(prev => ({ ...prev, modules: prev.modules.map(x => x.id === m.id ? { ...x, unlock_after_days: days } : x) }));
              toast.success(days ? `Module unlocks ${days} days after enrolment` : 'Module delay removed');
            } catch (error) {
              toast.error(error.response?.data?.detail || 'Delay was NOT saved');
            }
          }}
        />

        {/* Course Settings Modal */}
        <CourseSettingsModal
          open={showSettings}
          onClose={() => {
            if (isNewCourse && !course?.id) {
              navigate(coursesListPath);
            } else {
              setShowSettings(false);
            }
          }}
          course={course}
          onSave={course?.id ? handleUpdateCourse : handleCreateCourse}
          saving={saving}
        />
      </div>
    </DashboardLayout>
  );
};

function getDefaultElementTitle(type) {
  const titles = {
    video: 'Video Content',
    quiz: 'Quiz',
    case_study: 'Case Study',
    flashcard: 'Flashcards',
    fill_gaps: 'Fill the Gaps',
    drag_drop_task: 'Drag & Drop Task',
    matching: 'Matching Exercise',
    image_hotspot: 'Interactive Image',
    audio: 'Audio Content',
    downloadable: 'Downloadable Resource',
    reflection_journal: 'Reflection Journal',
    text: 'Text Content',
    trainer_tip: 'Trainer Tip',
    timeline: 'Interactive Timeline',
    label_diagram: 'Label the Diagram',
    sort_order: 'Sort Order',
    decision_tree: 'Scenario Decision Tree',
    progress_checkpoint: 'Progress Checkpoint',
  };
  return titles[type] || 'New Element';
}

function getDefaultConfig(type) {
  const configs = {
    video: { video: { source: 'youtube', url: '', completion_threshold: 0.9 } },
    quiz: { quiz: { questions: [], passing_score: 70, max_attempts: 3 } },
    case_study: { case_study: { title: '', scenario_text: '', reflective_questions: [] } },
    flashcard: { flashcard: { cards: [], shuffle: true } },
    fill_gaps: { fill_gaps: { template_text: '', word_options: [] } },
    drag_drop_task: { drag_drop_task: { task_description: '', drop_zones: [], draggable_items: [] } },
    matching: { matching: { pairs: [] } },
    image_hotspot: { image_hotspot: { image_url: '', hotspots: [] } },
    audio: { audio: { file_url: '' } },
    downloadable: { downloadable: { file_url: '', file_name: '' } },
    reflection_journal: { reflection_journal: { prompt: '' } },
    text: { text: { content: '' } },
    trainer_tip: { trainer_tip: { tip_type: 'welcome', text: "Hi there! Let\u2019s get started with this module. I\u2019ll be here to help you along the way." } },
    timeline: { timeline: { instructions: '', events: [{ id: '1', label: '', correct_position: 1 }] } },
    label_diagram: { label_diagram: { image_url: '', instructions: '', labels: [] } },
    sort_order: { sort_order: { instructions: '', items: [''] } },
    decision_tree: { decision_tree: { title: '', nodes: [{ id: 'start', text: '', choices: [{ label: '', next_node: '' }] }] } },
    progress_checkpoint: { progress_checkpoint: { message: '', questions: [{ question: '', options: ['', ''], correct: 0 }] } },
  };
  return configs[type] || {};
}

export default CourseBuilder;
